import type { IntegrationPlatform } from '@/lib/types/integrations';

/** Visual identity only. Connector capabilities belong in PLATFORM_CONFIGS. */
interface BrandDefinition {
  name: string;
  primary: string;
  palette?: Readonly<Record<string, string>>;
  dataUsage: 'accent' | 'logo-only' | 'neutral';
  source: string | null;
  evidence: 'palette' | 'asset' | 'website' | 'generic';
  logoPath?: string;
  /** Native width / height for wordmarks; symbols default to a square. */
  logoAspectRatio?: number;
}

export const INTEGRATION_BRANDS_REVIEWED_AT = '2026-10-08';

/**
 * Official digital colors, not print/CMYK values or a sorted chart palette.
 * Asset provenance and usage restrictions: docs/architecture/INTEGRATION_BRANDING.md.
 */
export const INTEGRATION_BRANDS: Record<IntegrationPlatform, BrandDefinition> =
  {
    walmart: {
      name: 'Walmart Marketplace',
      logoPath: '/logos/integrations/walmart.svg',
      primary: '#FFC220',
      dataUsage: 'logo-only',
      evidence: 'asset',
      source: 'https://www.walmart.com/',
    },
    ebay: {
      name: 'eBay',
      logoAspectRatio: 299.8 / 120.125,
      primary: '#0968F6',
      palette: {
        red: '#E53238',
        blue: '#0064D2',
        yellow: '#F5AF02',
        green: '#86B817',
      },
      dataUsage: 'accent',
      evidence: 'palette',
      source:
        'https://playbook.ebay.com/design-system/tokens/color-tokens?tab=core',
    },
    amazon: {
      name: 'Amazon',
      logoPath: '/logos/integrations/amazon.png',
      logoAspectRatio: 6110 / 2047,
      primary: '#FF6200',
      palette: { ink: '#161D26' },
      dataUsage: 'accent',
      evidence: 'asset',
      source: 'https://press.aboutamazon.com/logos',
    },
    shopify: {
      name: 'Shopify',
      primary: '#95BF46',
      palette: { shade: '#5F8E3E', white: '#FFFFFF' },
      dataUsage: 'accent',
      evidence: 'asset',
      source: 'https://www.shopify.com/brand-assets',
    },
    etsy: {
      name: 'Etsy',
      primary: '#F1641E',
      palette: { cream: '#F9F6F4' },
      dataUsage: 'accent',
      evidence: 'asset',
      logoPath: '/logos/integrations/etsy.svg',
      source: 'https://www.etsy.com/press',
    },
    poshmark: {
      name: 'Poshmark',
      primary: '#610722',
      dataUsage: 'accent',
      evidence: 'asset',
      logoPath: '/logos/integrations/poshmark.svg',
      logoAspectRatio: 106 / 13,
      source: 'https://poshmark.com/',
    },
    mercari: {
      name: 'Mercari',
      primary: '#FF0211',
      palette: { blue: '#4DC9FF', ink: '#222222' },
      dataUsage: 'accent',
      evidence: 'asset',
      logoPath: '/logos/integrations/mercari.png',
      source: 'https://about.mercari.com/press/press-kit/mercari/',
    },
    depop: {
      name: 'Depop',
      primary: '#E20020',
      palette: { black: '#000000', white: '#FFFFFF' },
      dataUsage: 'accent',
      evidence: 'asset',
      logoPath: '/logos/integrations/depop.svg',
      source: 'https://news.depop.com/media-centre/',
    },
    shipstation: {
      name: 'ShipStation',
      primary: '#00E664',
      palette: { ink: '#11003A' },
      dataUsage: 'accent',
      evidence: 'asset',
      source: 'https://www.shipstation.com/',
    },
    veeqo: {
      name: 'Veeqo',
      primary: '#2578FF',
      dataUsage: 'accent',
      evidence: 'asset',
      source: 'https://www.veeqo.com/',
    },
    quickbooks: {
      name: 'QuickBooks',
      primary: '#2CA01C',
      dataUsage: 'accent',
      evidence: 'palette',
      logoPath: '/logos/integrations/quickbooks.png',
      logoAspectRatio: 2345 / 600,
      source:
        'https://digitalasset.intuit.com/content/dam/intuit/sa/en_us/quickbooks/brand-design-guidelines/quickbooks-brand-partners-design-guidelines.pdf',
    },
    xero: {
      name: 'Xero',
      primary: '#13B5EA',
      dataUsage: 'logo-only',
      evidence: 'asset',
      source: 'https://www.xero.com/sg/media/downloads/',
    },
    zapier: {
      name: 'Zapier',
      primary: '#FF4A00',
      dataUsage: 'logo-only',
      evidence: 'palette',
      source:
        'https://partnerportal.zapier.com/sys/document/open/7Ot00000000003a00hE',
    },
    email: {
      name: 'Email',
      primary: 'var(--muted-foreground)',
      dataUsage: 'neutral',
      evidence: 'generic',
      source: null,
    },
    plaid: {
      name: 'Plaid',
      primary: '#111112',
      dataUsage: 'accent',
      evidence: 'website',
      source: 'https://plaid.com/press/',
      logoPath: '/logos/integrations/plaid.svg',
      logoAspectRatio: 77 / 29,
    },
    stripe: {
      name: 'Stripe',
      logoAspectRatio: 512 / 214,
      primary: '#635BFF',
      dataUsage: 'accent',
      evidence: 'asset',
      source: 'https://stripe.com/newsroom/information',
    },
    paypal: {
      name: 'PayPal',
      primary: '#002991',
      palette: { overlap: '#008CFF', light: '#60CDFF' },
      dataUsage: 'accent',
      evidence: 'asset',
      source: 'https://newsroom.paypal-corp.com/media-resources',
    },
    square: {
      name: 'Square',
      primary: '#000000',
      dataUsage: 'accent',
      evidence: 'asset',
      source: 'https://developer.squareup.com/docs/brand-guidelines',
    },
    rippling: {
      name: 'Rippling',
      primary: '#4A0039',
      palette: { black: '#000000', white: '#FFFFFF' },
      dataUsage: 'accent',
      evidence: 'website',
      logoPath: '/logos/integrations/rippling.svg',
      logoAspectRatio: 127 / 18,
      source: 'https://www.rippling.com/en-IE/company/press',
    },
  };

const ALIASES: Record<string, IntegrationPlatform> = {
  quickbooksonline: 'quickbooks',
  bankconnections: 'plaid',
  emailprocessing: 'email',
};

export function getIntegrationBrand(platform: string) {
  const key = platform
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  const id = Object.hasOwn(ALIASES, key) ? ALIASES[key] : key;
  if (!Object.hasOwn(INTEGRATION_BRANDS, id)) return undefined;
  const platformId = id as IntegrationPlatform;
  return { id: platformId, ...INTEGRATION_BRANDS[platformId] };
}

/** Brand color for data attribution; restrictions and unknown sources stay neutral. */
export function getChannelColor(channel: string): string {
  const brand = getIntegrationBrand(channel);
  return brand?.dataUsage === 'accent'
    ? brand.primary
    : 'var(--muted-foreground)';
}

export function getChannelLabel(channel: string): string {
  if (channel === 'direct_invoices') return 'Direct invoices';
  if (channel === 'other') return 'Other revenue';
  return getIntegrationBrand(channel)?.name ?? channel.replace(/_/g, ' ');
}
